/**
 * pastoralTrajectoryAnalyzer.js
 * Pure function — derives spiritual/pastoral trajectory from current relationship state
 * and longitudinal signal history.
 *
 * Trajectories (no analytics language):
 *   creciendo       — consistent followup + active discipleship
 *   estable         — regular contact, no risk signals
 *   en_proceso      — active spiritual process (study, prayer, baptism)
 *   distanciamiento — declining contact + rising risk
 *   en_riesgo       — pastoral risk present, irregular followup
 *   aislado         — no contact + high risk
 *   restaurando     — was absent/at-risk, returning to contact
 *   desconocido     — insufficient history
 */

/** @typedef {"creciendo"|"estable"|"en_proceso"|"distanciamiento"|"en_riesgo"|"aislado"|"restaurando"|"desconocido"} TrajectoryStage */

const STAGE_NARRATIVES = {
  creciendo:       "Seguimiento consistente y proceso espiritual activo.",
  estable:         null, // intentionally silent — stable is the norm
  en_proceso:      "Proceso espiritual activo — acompañamiento en curso.",
  distanciamiento: "Señales de distanciamiento progresivo.",
  en_riesgo:       "Requiere atención pastoral.",
  aislado:         "Sin contacto pastoral reciente — convendría retomar el vínculo.",
  restaurando:     "Retomando el contacto después de un período de distancia.",
  desconocido:     null,
};

/**
 * Determine if the signal history shows a worsening trend.
 * Returns true when at least 2 of the last 3 signals are "at-risk" tones.
 *
 * @param {import("./relationalMemoryEngine").PastoralSignal[]} signals
 * @returns {boolean}
 */
function isWorsening(signals) {
  if (!signals?.length) return false;
  const HIGH_RISK_TONES = new Set(["followup_urgent", "sensitive"]);
  const recent   = signals.slice(0, 3);
  const atRisk   = recent.filter((s) => HIGH_RISK_TONES.has(s.tone)).length;
  return atRisk >= 2;
}

/**
 * Determine if the signal history shows recovery (was at-risk, now improving).
 * @param {import("./relationalMemoryEngine").PastoralSignal[]} signals
 * @returns {boolean}
 */
function isRestoring(signals) {
  if (!signals || signals.length < 2) return false;
  const prev    = signals[1]?.tone ?? null;
  const current = signals[0]?.tone ?? null;
  const WAS_RISK = new Set(["followup_urgent", "followup_needed", "sensitive"]);
  const IS_OK    = new Set(["neutral", "accompany"]);
  return WAS_RISK.has(prev) && IS_OK.has(current);
}

/**
 * @param {{
 *   state:   ReturnType<import("./relationshipStateResolver").resolveRelationshipState>,
 *   history: import("./relationalMemoryEngine").RelationalRecord | null,
 *   tone:    string,
 * }} params
 * @returns {{
 *   stage:     TrajectoryStage,
 *   narrative: string | null,
 *   hasChange: boolean,
 * }}
 */
export function analyzeTrajectory({ state, history, tone }) {
  const signals    = history?.signals ?? [];
  const hasHistory = signals.length >= 2;

  const { followup_frequency, pastoral_risk, attendance_signal } = state;

  // ── Restoration detection (priority — positive signal) ───────────────────
  if (hasHistory && isRestoring(signals)) {
    return _build("restaurando", hasHistory);
  }

  // ── Isolation ─────────────────────────────────────────────────────────────
  if (pastoral_risk === "high" && followup_frequency === "absent") {
    return _build("aislado", hasHistory);
  }

  // ── Worsening trend ───────────────────────────────────────────────────────
  if (hasHistory && isWorsening(signals)) {
    return _build("distanciamiento", hasHistory);
  }

  // ── General risk ─────────────────────────────────────────────────────────
  if (pastoral_risk === "high" || pastoral_risk === "medium") {
    return _build("en_riesgo", hasHistory);
  }

  // ── Declining attendance signal ───────────────────────────────────────────
  if (attendance_signal === "declining") {
    return _build("distanciamiento", hasHistory);
  }

  // ── Baptism process ───────────────────────────────────────────────────────
  if (tone === "accompany") {
    return _build("en_proceso", hasHistory);
  }

  // ── Growth ────────────────────────────────────────────────────────────────
  if (followup_frequency === "regular" && pastoral_risk === "none") {
    if (attendance_signal === "consistent" || (hasHistory && !isWorsening(signals))) {
      return _build("creciendo", hasHistory);
    }
  }

  // ── Stable ───────────────────────────────────────────────────────────────
  if (followup_frequency !== "absent" && pastoral_risk === "none") {
    return _build("estable", hasHistory);
  }

  return _build("desconocido", hasHistory);
}

function _build(stage, hasChange) {
  return {
    stage,
    narrative: STAGE_NARRATIVES[stage] ?? null,
    hasChange,
  };
}
